<template>
  <div
    v-click-outside="clickOutsideConfig"
    class="w-full min-w-0 max-w-full"
    :class="{ 'relative z-30': isSuggestionShown }"
  >
    <div
      ref="activatorRef"
      data-test="participants-select-root"
      :class="{
        'border-none! p-0!': noBorder,
        'border! border-neg-base-def!': invalidField,
      }"
      class="relative participants-select-root min-h-6 min-w-0 max-w-full px-1 py-0.75 border bg-bg-base-bg1 border-bor-20 rounded-8"
      @click="onFocus($event)"
    >
      <div
        class="max-h-46.5 flex flex-wrap gap-1 h-full min-w-0 max-w-full overflow-x-hidden overflow-y-auto items-center px-px"
        :class="{'pr-1' : chips.length > 1}"
      >
        <div
          v-if="isForModerators || isForTemporaryParticipants"
          data-test="hide-list"
          class="bg-surf-brand-soft-def text-surf-brand-base-def flex items-center gap-x-1 py-1 pl-2 pr-3 text-1316 cursor-pointer"
          @click.stop="emit('hideList')"
        >
          <wui-icon
            name="select"
            class="h-4! w-4! rotate-90 text-surf-brand-base-def!"
          />
          <p class="mx-auto">
            {{ isForModerators ? $t('chat.moderators') : $t('common.temporaryParticipants') }}
          </p>
        </div>
        <template
          v-for="chip in chips"
          :key="chip.id"
        >
          <div
            class="max-w-full min-w-0"
            @click.stop
          >
            <slot
              :id="chip.id"
              name="chip"
              :chip="chip"
              :participant="chip.item"
              :display-suffix="chip.displaySuffix"
              :icon="iconForChip"
              :disabled="disabled"
              :is-for-temporary="isForTemporaryParticipants"
              :on-delete="() => onDelete(chip.id)"
            >
              <participant-chip
                :participant="chip.item as SelectItem<T>"
                :id-key="idKey as (K | K[])"
                :icon="iconForChip"
                :display-suffix="chip.displaySuffix!"
                :is-for-temporary="isForTemporaryParticipants!"
                :disabled="disabled!"
                @delete="onDelete(chip.id)"
              />
            </slot>
          </div>
        </template>
        <wui-input
          v-if="!multiple && model.length === 0"
          v-model="text"
          data-test="input-not-multiple"
          :error="danger!"
          :disabled="disabled ?? false"
          :size="24 as WuiInputSize"
          :required="required"
          :placeholder="$t('calls.typeParticipantData')"
          class="grow"
          input-class="pl-1 border-none! shadow-none! bg-transparent! font-light text-1324"
          @focus="onFocus"
          @keyup.enter.stop="onTextSuggestionSelect(true)"
        />
        <wui-input
          v-if="model.length >= 0 && multiple"
          id="inputForParticipants"
          v-model="text"
          :size="24 as WuiInputSize"
          :disabled="disabled ?? false"
          data-test="input"
          :error="danger!"
          autocomplete="off"
          :required="required"
          :placeholder="!modelValue.length ? t('calls.typeParticipantData') : ''"
          class="inner-input inner-input-participants min-w-16 basis-16 flex-1"
          input-class="border-none! shadow-none! bg-transparent! font-light text-1324 pl-[5px] pr-3 py-0.5 focus:outline-none"
          @focus="onFocus"
          @keyup.enter.stop="onTextSuggestionSelect(true)"
        />
      </div>
    </div>
    <p
      v-if="cantAddInvalidValueShowTitle && !isTable"
      class="text-neg-base-def text-1112 mt-1 ml-4 text-wrap max-w-[400px]"
    >
      {{ $t('common.invalidValue') }}. {{ $t('common.datasForValues') }}
    </p>
    <p
      v-if="cantAddInvalidValueForModeratorsShowTitle"
      class="text-neg-base-def text-1112 mt-1 ml-4 text-wrap max-w-[400px]"
    >
      {{ $t('common.moderatorsCantBe') }}
    </p>
    <p
      v-if="(isValueAlreadyAdded || isTextValueExcluded) && !isTable"
      class="text-neg-base-def text-1112 mt-1 ml-4 text-wrap max-w-[400px]"
    >
      {{ $t('common.valueAlreadyBeenAdded') }}
    </p>
    <p
      v-if="cantBeEmpty && inputCantBeEmpty && !isTable"
      class="text-neg-base-def text-1112 mt-1 ml-4 text-wrap max-w-[400px]"
    >
      {{ $t('notifications.emptyDataForm') }}
    </p>
    <teleport :to="teleportTarget">
      <div
        v-if="isSuggestionsPanelVisible"
        ref="suggestionsRef"
        data-test="suggestions"
        data-participants-select-suggestions
        class="bg-elev-dropdown-bg border-bor-divider shadow-overlay fixed z-9999 border max-h-64 p-1 overflow-y-auto"
        :style="suggestionsPanelStyle"
      >
        <loader-bar
          v-show="isLoading"
          class="sticky z-20 top-0"
        />
        <div
          v-if="isTextSuggestionSelectVisible"
          class="root flex py-2 px-4 cursor-pointer hover:bg-surf-neut-soft-hov"
          @click.stop="onTextSuggestionSelect()"
        >
          <div class="grid gap-y-2">
            <p
              class="text-surf-brand-base-def"
            >
              {{ text }}
            </p>

            <p
              class="text-1316 text-surf-brand-base-def"
            >
              {{
                isTextANumber ? t('common.useThisNumber') : $t('common.useThisAddress')
              }}
            </p>
          </div>
        </div>
        <virtual-scroller
          :items="suggestionsForScroller"
          :item-size="64"
          page-mode
          key-field="scrollerKey"
          :buffer="128"
          @scroll-end="onScrollEndReached"
        >
          <template #default="{ item }">
            <div
              ref="suggestionRef"
              @click.stop="onSelect(item)"
            >
              <slot
                name="suggestion"
                :suggestion="item"
                :text="text"
                :max-width="suggestionsWidth"
              />
            </div>
          </template>
        </virtual-scroller>
      </div>
    </teleport>
  </div>
</template>

<script setup lang="ts" generic="T, K extends keyof T">
import { ClickOutside as vClickOutside, Direction, SelectItem, WuiIcon, WuiInput, WuiInputSize } from '@wui/common-library'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, Ref, shallowRef, toRefs, useAttrs, watch } from 'vue'

import { LoaderBar } from '@/shared/components/loaders'
import { VirtualScroller } from '@/shared/components/virtual-scroller'
import { useLocalization } from '@/shared/composables/localization'
import { usePositionPopUpByDirection } from '@/shared/composables/use-position-popup'
import { isBotCatalogSearchQuery } from '@/shared/utils/contacts'

import { isPhoneNumber, isEmail, isPhoneToken, parsePhoneToken } from '../lib'
import { ConferenceParticipantType } from '../types'

import ParticipantChip from './participant-chip.vue'

/* eslint-disable vue/require-default-prop -- allItems, idKey, onEndReachedCb, chipValidatorFn omitted from defaults to satisfy exactOptionalPropertyTypes */
const props = withDefaults(defineProps<{
  modelValue: (T[K] | string)[]
  items?: SelectItem<T>[]
  allItems?: SelectItem<T>[]
  idKey?: keyof T | (keyof T)[]
  excludedTypes?: ConferenceParticipantType[]
  multiple?: boolean
  suggestionsWidth?: number,
  fieldsSearchBy?: (keyof T)[],
  isForModerators?: boolean,
  isForTemporaryParticipants?: boolean
  cantBeEmpty?: boolean,
  isTable?: boolean,
  disabled?: boolean,
  danger?: boolean,
  onEndReachedCb?: () => Promise<void>
  chipValidatorFn?: (id: string) => boolean
  excludedIdentities?: string[]
  noBorder?: boolean
  serverSideFilter?: boolean
}>(), {
  items: () => [],
  excludedTypes: () => [],
  multiple: false,
  suggestionsWidth: 280,
  fieldsSearchBy: () => [],
  isForModerators: false,
  isForTemporaryParticipants: false,
  cantBeEmpty: false,
  isTable: false,
  disabled: false,
  danger: false,
  excludedIdentities: () => [],
  noBorder: false,
  serverSideFilter: false,
})
/* eslint-enable vue/require-default-prop */

const emit = defineEmits<{
  (event: 'update:modelValue', value: (T[K] | string)[]): void;
  (event: 'hideList'): void
}>()

const { items, allItems } = toRefs(props)

const selectedItemsByIdentity = shallowRef(new Map<string, SelectItem<T>>())

/** Справочник для чипов: имена не зависят от текущей поисковой выдачи. */
const itemsForChips = computed(() => allItems.value?.length ? allItems.value : items.value)

const isSuggestionShown = ref(false)
const cantAddInvalidValueShowTitle = ref(false)
const cantAddInvalidValueForModeratorsShowTitle = ref(false)
const isValueAlreadyAdded = ref(false)
const inputCantBeEmpty = ref(false)
const isLoading = ref(false)
const activatorRef = ref<HTMLElement | null>(null)
const suggestionsRef = ref<HTMLElement | null>(null)
const teleportTarget = ref<HTMLElement | string>('body')

const { popupStyles, updatePosition } = usePositionPopUpByDirection({
  activator: activatorRef,
  popup: suggestionsRef,
  direction: Direction.BOTTOM,
  offset: 6,
})

const updateTeleportTarget = () => {
  teleportTarget.value = document.fullscreenElement as HTMLElement ?? document.body
}

const scheduleSuggestionsPositionUpdate = async () => {
  if (!isSuggestionsPanelVisible.value) {
    return
  }
  await nextTick()
  await updatePosition()
}

const iconForChip = computed(() => {
  return props.isForModerators ? 'moderAvatarAltBlack' : 'userRoundF'
})

const isTextSuggestionSelectVisible = computed (() => {
  return (isTextANumber.value || isTextAnEmail.value) && !props.isForModerators && suggestions.value.length === 0 && !isTextValueExcluded.value
})

const isSuggestionsPanelVisible = computed(() => {
  return isSuggestionShown.value && (
    suggestions.value.length > 0 ||
    ((isTextANumber.value || isTextAnEmail.value) && !props.isForModerators && !props.danger && !isTextValueExcluded.value)
  )
})

const suggestionsPanelStyle = computed(() => ({
  width: `${props.suggestionsWidth}px`,
  ...popupStyles.value,
}))

const clickOutsideConfig = {
  handler: () => onBlur(),
  include: () => [suggestionsRef.value].filter((el): el is HTMLElement => Boolean(el)),
}

const invalidField = computed(() => {
  return cantAddInvalidValueShowTitle.value
  || cantAddInvalidValueForModeratorsShowTitle.value
  || isValueAlreadyAdded.value
  || inputCantBeEmpty.value
  || props.danger
  || isTextValueExcluded.value
})

const attrs = useAttrs()
const { t } = useLocalization()

const text = defineModel<string>('text', { default: '' })
const model = ref([...props.modelValue]) as Ref<(T[K] | string)[]>
const suggestions: Ref<SelectItem<T>[]> = ref([])
type SuggestionScrollerItem = SelectItem<T> & { scrollerKey: string }
type ParticipantIdentity = T[K] | string | undefined

const isBotSearchQuery = computed(() => isBotCatalogSearchQuery(text.value))

/** При server-side или `@`-поиске ботов саджесты только из `items`. */
const itemsForSuggestions = computed(() => {
  if (props.serverSideFilter || isBotSearchQuery.value) return items.value
  return allItems.value?.length ? allItems.value : items.value
})

const idKeys = computed(() => {
  if (!props.idKey) return []
  return Array.isArray(props.idKey) ? props.idKey : [props.idKey]
})

const getValueByIdKey = (item: T): ParticipantIdentity => {
  if (!idKeys.value.length) {
    return item as T[K]
  }

  const itemRecord = item as Record<string, unknown>
  for (const key of idKeys.value) {
    const value = itemRecord[key as string]
    if (value) {
      return value as T[K]
    }
  }

  return itemRecord[idKeys.value[0] as string] as T[K]
}

const normalizeIdentity = (value: unknown): string | undefined => {
  if (value === undefined || value === null) {
    return undefined
  }
  const normalized = String(value).trim().toLowerCase()
  return normalized || undefined
}

const getRecordIdentities = (record: Record<string, unknown>): string[] => {
  const identities = new Set<string>()
  const add = (value: unknown) => {
    const normalized = normalizeIdentity(value)
    if (normalized) {
      identities.add(normalized)
    }
  }

  add(getValueByIdKey(record as T))
  add(record.internalNumber)
  add(record.mobilePhone)
  add(record.imLogin)
  idKeys.value.forEach((key) => add(record[key as string]))

  return Array.from(identities)
}

const getRecordEmail = (record: Record<string, unknown>): string | undefined => (
  normalizeIdentity(record.email)
)

const getTypedEmailsInModel = (): Set<string> => {
  const emails = new Set<string>()
  model.value.forEach((id) => {
    if (typeof id !== 'string' || !isEmail(id)) {
      return
    }
    const normalized = normalizeIdentity(id)
    if (normalized) {
      emails.add(normalized)
    }
  })
  return emails
}

const getSelectedContactEmails = (): Set<string> => {
  const emails = new Set<string>()
  const source = itemsForChips.value

  model.value.forEach((id) => {
    if (typeof id === 'string' && isEmail(id)) {
      return
    }

    const matchedItem = source.find((item: SelectItem<T>) => {
      const itemId = getValueByIdKey(item.value)
      return itemId === id || normalizeIdentity(itemId) === normalizeIdentity(id)
    })
    const email = matchedItem
      ? getRecordEmail(matchedItem.value as Record<string, unknown>)
      : undefined
    if (email) {
      emails.add(email)
    }
  })

  return emails
}

const rememberItem = (item: SelectItem<T>) => {
  if (typeof item.value === 'string') return

  const identities = getRecordIdentities(item.value as Record<string, unknown>)
  const normalizedId = normalizeIdentity(getValueByIdKey(item.value))
  if (normalizedId && !identities.includes(normalizedId)) {
    identities.push(normalizedId)
  }
  if (!identities.length) return

  const alreadyCached = identities.every((identity) => selectedItemsByIdentity.value.get(identity) === item)
  if (alreadyCached) return

  const next = new Map(selectedItemsByIdentity.value)
  identities.forEach((identity) => next.set(identity, item))
  selectedItemsByIdentity.value = next
}

const findContactItem = (
  id: T[K] | string,
  source: SelectItem<T>[],
): { item: SelectItem<T>, displaySuffix?: string } | undefined => {
  const phoneParts = typeof id === 'string' ? parsePhoneToken(id) : null
  const normalizedPhoneBase = phoneParts?.normalizedBase

  const strictItem = source.find((item: SelectItem<T>) => getValueByIdKey(item.value) === id)
  if (strictItem) {
    return phoneParts?.suffix
      ? { item: strictItem, displaySuffix: phoneParts.suffix }
      : { item: strictItem }
  }

  if (typeof id === 'string' && phoneParts?.suffix) {
    const normalize = (v: string) => v.startsWith('+') ? v.slice(1) : v

    const baseMatchedItem = source.find((item: SelectItem<T>) => {
      if (idKeys.value.length) {
        const hasMatchedKey = idKeys.value.some((key) => {
          const value = (item.value as Record<string, unknown>)[key as string]
          return typeof value === 'string' && normalize(value) === normalizedPhoneBase
        })
        if (hasMatchedKey) return true
      }

      const internalNumber = (item.value as Record<string, unknown>).internalNumber
      return typeof internalNumber === 'string' && normalize(internalNumber) === normalizedPhoneBase
    })

    if (baseMatchedItem) {
      return { item: baseMatchedItem, displaySuffix: phoneParts.suffix }
    }
  }

  if (typeof id === 'string') {
    const normalizedId = normalizeIdentity(id)
    if (normalizedId && isEmail(id)) {
      const byEmail = source.find((item: SelectItem<T>) => (
        getRecordEmail(item.value as Record<string, unknown>) === normalizedId
      ))
      if (byEmail) {
        return { item: byEmail }
      }
    }

    const identityMatchedItem = normalizedId
      ? source.find((item: SelectItem<T>) => (
        getRecordIdentities(item.value as Record<string, unknown>).includes(normalizedId)
      ))
      : undefined

    if (identityMatchedItem) {
      return { item: identityMatchedItem }
    }

    const cachedItem = normalizedId ? selectedItemsByIdentity.value.get(normalizedId) : undefined
    if (cachedItem) {
      return phoneParts?.suffix
        ? { item: cachedItem, displaySuffix: phoneParts.suffix }
        : { item: cachedItem }
    }
  }

  return undefined
}

const getModelIdentities = (): Set<string> => {
  const identities = new Set<string>()
  const localItems = itemsForChips.value

  model.value.forEach((id) => {
    const normalizedId = normalizeIdentity(id)
    if (normalizedId) {
      identities.add(normalizedId)
    }

    if (typeof id !== 'string') {
      return
    }

    const matchedItem = localItems.find((item: SelectItem<T>) => {
      const itemId = getValueByIdKey(item.value)
      return itemId === id || normalizeIdentity(itemId) === normalizedId
    })
    if (matchedItem) {
      getRecordIdentities(matchedItem.value as Record<string, unknown>).forEach((key) => identities.add(key))
      return
    }

    const byPhone = localItems.find((item: SelectItem<T>) => {
      const record = item.value as Record<string, unknown>
      return getRecordIdentities(record).includes(normalizedId!)
    })
    if (byPhone) {
      getRecordIdentities(byPhone.value as Record<string, unknown>).forEach((key) => identities.add(key))
    }
  })

  return identities
}

const isAlreadySelected = (candidateIdentities: string[]): boolean => {
  if (!candidateIdentities.length) {
    return false
  }
  const selected = getModelIdentities()
  return candidateIdentities.some((identity) => selected.has(identity))
}

const getSuggestionScrollerKey = (item: SelectItem<T>, index: number): string => {
  const id = getValueByIdKey(item.value)
  if (id !== undefined && id !== null && id !== '') {
    return String(id)
  }

  if (typeof item.value === 'string') {
    return item.value
  }

  return `${item.label ?? ''}-${index}`
}

const suggestionsForScroller = computed<SuggestionScrollerItem[]>(() =>
  suggestions.value.map((item, index) => ({
    ...item,
    scrollerKey: getSuggestionScrollerKey(item, index),
  })),
)

const required = computed(() => 'required' in attrs && !model.value.length)

const chips = computed(() => {
  const chips: { id: string, item: SelectItem<T | string>, displaySuffix?: string }[] = []
  const seenIdentities = new Set<string>()

  model.value.forEach((id) => {
    let chip: { id: string, item: SelectItem<T | string>, displaySuffix?: string } | undefined
    const resolved = findContactItem(id, itemsForChips.value)

    if (resolved) {
      chip = resolved.displaySuffix
        ? {
          id: id as string,
          item: resolved.item,
          displaySuffix: resolved.displaySuffix,
        }
        : {
          id: id as string,
          item: resolved.item,
        }
    } else if (typeof id === 'string') {
      if (isEmail(id) || isPhoneNumber(id) || isPhoneToken(id)) {
        chip = {
          id: id as string,
          item: {
            label: id as string,
            value: id as string,
          },
        }
      } else if (props.chipValidatorFn?.(id)) {
        chip = {
          id: id as string,
          item: {
            label: id as string,
            value: id as string,
          },
        }
      }
    }

    if (!chip) {
      return
    }

    const chipIdentities = typeof chip.item.value === 'string'
      ? [normalizeIdentity(chip.item.value)].filter((value): value is string => Boolean(value))
      : getRecordIdentities(chip.item.value as Record<string, unknown>)

    if (chipIdentities.some((identity) => seenIdentities.has(identity))) {
      return
    }
    chipIdentities.forEach((identity) => seenIdentities.add(identity))
    chips.push(chip)
  })

  return chips
})

const availableSuggestions = computed(() => {
  const selectItems = itemsForSuggestions.value
  const excluded = props.excludedIdentities.map((id) => id.trim().toLowerCase())
  const selectedIdentities = getModelIdentities()
  const typedEmails = getTypedEmailsInModel()
  return selectItems.filter((item: SelectItem<T>) => {
    const id = getValueByIdKey(item.value)
    if (id === undefined) return false
    const itemIdentities = getRecordIdentities(item.value as Record<string, unknown>)
    if (itemIdentities.some((identity) => selectedIdentities.has(identity))) return false
    const itemEmail = getRecordEmail(item.value as Record<string, unknown>)
    if (itemEmail && typedEmails.has(itemEmail)) return false
    if (!excluded.length) return true
    return !itemIdentities.some((i) => excluded.includes(i))
  })
})

const filterSuggestionsBySearch = (searchValue: string): SelectItem<T>[] => {
  return availableSuggestions.value.filter((item: SelectItem<T>) => {
    if (!props.fieldsSearchBy.length) {
      return item.label.toLowerCase().includes(searchValue.toLowerCase())
    }

    for (const field of props.fieldsSearchBy) {
      if (!field) {
        continue
      }

      const value = item.value[field] as string
      if (!value) {
        continue
      }

      if (value.toLowerCase().includes(searchValue.toLowerCase())) {
        return true
      }
    }

    return false
  })
}

const applySuggestions = (searchValue: string) => {
  if ((props.serverSideFilter || isBotCatalogSearchQuery(searchValue)) && searchValue) {
    suggestions.value = availableSuggestions.value
    return
  }

  suggestions.value = filterSuggestionsBySearch(searchValue)
}

const isTextANumber = computed(() => !props.excludedTypes.includes('externalNumber') && (isPhoneNumber(text.value) || isPhoneToken(text.value)))

const isTextAnEmail = computed(() => !props.excludedTypes.includes('email') && isEmail(text.value))

const isTextValueExcluded = computed(() => {
  if (!text.value || !props.excludedIdentities?.length) return false
  return props.excludedIdentities.some((id) => id.trim().toLowerCase() === text.value.trim().toLowerCase())
})

const onFocus = (event?: Event) => {
  if (!props.multiple && model.value.length === 1) {
    return
  }
  isSuggestionShown.value = true
  if (props.disabled) {
    return
  }
  if (event) {
    /*
     эти 2 строчки нужны для того, чтобы пользователь мог ткнуть в любое место в инпуте и фокус устанавливался точно туда
     а currentTarget нам нужен потому что таких инпутов на странице может быть много (черно-белые списки например)
     */
    const inputWhichWasClicked = event.currentTarget as Element
    inputWhichWasClicked?.querySelector('#inputForParticipants')?.querySelector('input')?.focus()
  }
  if (text.value || props.excludedTypes.includes('subscriber')) {
    void scheduleSuggestionsPositionUpdate()
    return
  }
  suggestions.value = availableSuggestions.value
  void scheduleSuggestionsPositionUpdate()
}

const closeSuggestions = () => {
  isSuggestionShown.value = false
}

const onBlur = () => {
  closeSuggestions()
}

watch(() => props.disabled, (disabled) => {
  if (disabled) {
    closeSuggestions()
  }
})

const onSelect = (suggestion: SelectItem<T>) => {
  if (inputCantBeEmpty.value) {
    inputCantBeEmpty.value = false
  }
  const rawText = text.value
  const phoneParts = parsePhoneToken(rawText)
  const suggestionIdentities = getRecordIdentities(suggestion.value as Record<string, unknown>)

  if (isAlreadySelected(suggestionIdentities) || (phoneParts?.suffix && isAlreadySelected(
    [normalizeIdentity(rawText)].filter((value): value is string => Boolean(value)),
  ))) {
    isValueAlreadyAdded.value = true
    return
  }

  // If user typed a composite token like `base*4` / `base#`,
  // preserve it as-is so suffix isn't lost.
  if (phoneParts?.suffix) {
    model.value.push(rawText)
  } else {
    const value = getValueByIdKey(suggestion.value)
    if (value !== undefined) model.value.push(value)
  }
  rememberItem(suggestion)
  text.value = ''
  closeSuggestions()
  emit('update:modelValue', model.value)
}

const onTextSuggestionSelect = (isEnterPressed = false) => {
  if (props.isForModerators) {
    cantAddInvalidValueForModeratorsShowTitle.value = true
    return
  }
  const phoneParts = parsePhoneToken(text.value)
  if (text.value && (isEmail(text.value) || isPhoneNumber(text.value) || phoneParts)) {
    const typedIdentities = [normalizeIdentity(text.value)].filter((value): value is string => Boolean(value))
    if (isAlreadySelected(typedIdentities)) {
      isValueAlreadyAdded.value = true
      return
    }
    if (isEmail(text.value)) {
      const typedEmail = normalizeIdentity(text.value)
      if (
        typedEmail
        && (getTypedEmailsInModel().has(typedEmail) || getSelectedContactEmails().has(typedEmail))
      ) {
        isValueAlreadyAdded.value = true
        return
      }
    }
    if (props.excludedIdentities?.some((id) => id.trim().toLowerCase() === text.value.trim().toLowerCase())) {
      isValueAlreadyAdded.value = true
      return
    }

    // If user typed a composite token (`base*4` / `base#`), preserve it as-is
    // even when the UI has exactly one suggestion match.
    if (isEnterPressed && suggestions.value.length === 1 && !phoneParts?.suffix) {
      const suggestion = suggestions.value[0]
      if (suggestion && isAlreadySelected(getRecordIdentities(suggestion.value as Record<string, unknown>))) {
        isValueAlreadyAdded.value = true
        return
      }
      const value = suggestion ? getValueByIdKey(suggestion.value) : undefined
      if (value !== undefined) model.value.push(value)
      if (suggestion) rememberItem(suggestion)
    } else {
      model.value.push(text.value)
    }

    text.value = ''
    emit('update:modelValue', model.value)
  } else {
    cantAddInvalidValueShowTitle.value = true
  }
  isSuggestionShown.value = false
}

const onDelete = (id: T[keyof T] | string) => {
  model.value = model.value.filter(i => i !== id)
  emit('update:modelValue', model.value)
  if (props.cantBeEmpty && !model.value.length) {
    inputCantBeEmpty.value = true
  }
}

const onScrollEndReached = async () => {
  if (!props.onEndReachedCb) return

  try {
    isLoading.value = true
    await props.onEndReachedCb()
  } catch (e) {
    console.error(e)
  } finally {
    isLoading.value = false
  }
}

watch(text, async (v) => {
  if (props.excludedTypes.includes('subscriber')) {
    return
  }
  if (cantAddInvalidValueForModeratorsShowTitle.value) {
    cantAddInvalidValueForModeratorsShowTitle.value = false
  }
  if (cantAddInvalidValueShowTitle.value) {
    cantAddInvalidValueShowTitle.value = false
  }
  if (isValueAlreadyAdded.value) {
    isValueAlreadyAdded.value = false
  }
  if (inputCantBeEmpty.value) {
    inputCantBeEmpty.value = false
  }
  if (!v || model.value.length === 1 && !props.multiple) {
    suggestions.value = []
    return
  }

  const phoneParts = parsePhoneToken(v)
  const searchValue = phoneParts?.normalizedBase ?? v

  applySuggestions(searchValue)
  await scheduleSuggestionsPositionUpdate()
})

watch(model, () => {
  suggestions.value = []
}, {
  deep: true,
})

watch(() => props.modelValue, (v) => {
  model.value = v
}, {
  deep: true,
})

watch(
  [model, items, allItems],
  () => {
    const source = [...(allItems.value ?? []), ...items.value]
    model.value.forEach((id) => {
      const found = findContactItem(id, source)
      if (found) rememberItem(found.item)
    })
  },
  { deep: true, immediate: true },
)

let initialChangePassed = false
watch(items, async () => {
  if (!initialChangePassed) {
    initialChangePassed = true
    return
  }

  const phoneParts = parsePhoneToken(text.value)
  const searchValue = phoneParts?.normalizedBase ?? text.value

  applySuggestions(searchValue)
  await scheduleSuggestionsPositionUpdate()
})

watch(isSuggestionsPanelVisible, (isVisible) => {
  if (isVisible) {
    void scheduleSuggestionsPositionUpdate()
  }
})

onMounted(() => {
  updateTeleportTarget()
  document.addEventListener('fullscreenchange', updateTeleportTarget)
  window.addEventListener('resize', scheduleSuggestionsPositionUpdate)
  window.addEventListener('scroll', scheduleSuggestionsPositionUpdate, true)
})

onBeforeUnmount(() => {
  document.removeEventListener('fullscreenchange', updateTeleportTarget)
  window.removeEventListener('resize', scheduleSuggestionsPositionUpdate)
  window.removeEventListener('scroll', scheduleSuggestionsPositionUpdate, true)
})
</script>
